import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { Objects } from "src/modules/utils/shared/objects";
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { EnaioCertificateService } from '../../services/enaio-certificate.service';
import { ClassDuty } from '../../shared/class-duty.entity';
import { ClassRegister } from '../../shared/class-register.entity';
import { Pupil } from '../../shared/pupil.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { Utils } from 'src/modules/utils/shared/utils';
import { Arrays } from 'src/modules/utils/shared/arrays';

@Component({
    selector: 'app-class-duties',
    templateUrl: './class-duties.component.html',
    styleUrls: ['./class-duties.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ClassDutiesComponent extends ComponentView {

    service = inject(EnaioCertificateService);

    neededParams: ObjectOfSimpleDataTypes = { id: "number" };
    id: number;

    schoolClassId: number;
    classRegister: ClassRegister;
    showDivider = false;

    dutyNames: string[] = [];
    rows: ClassDuty[] = [];
    orgRows: ClassDuty[] = [];
    classPupils: Pupil[];
    pupilsGui: FrontendFieldListItem[] = [];
    pupilsGuiSubstitute: FrontendFieldListItem[] = [];
    isPupil: OrdinaryObject<boolean> = {};
    classDuties: ClassDuty[] = [];

    table: TableData;

    async initParams(): Promise<boolean> {
        this.fullScreenComponent = false;
        this.classRegister = await this.service.restGetClassRegister(this.id, ["duties"], null, true);
        this.dutyNames = await RestEndpoint.main().query({ year: this.classRegister.enaioSchoolClass.getYearNumber(), isHalfYear: false }).run("api/cer/classregister/dutynames").listStrings();
        this.classPupils = [new Pupil(), ...await this.service.restGetPupils(this.classRegister.schoolClassId)
        ];
        this.pupilsGui = this.classPupils.map(d => new FrontendFieldListItem(d.id, d.id == 0 ? "" : d.getFullName()));
        this.pupilsGuiSubstitute = this.pupilsGui.filter(p => p.value != 0);

        this.rows = [];
        for (let entry of this.classRegister.duties) {
            this.classDuties.push(entry);
        }

        for (let dutyname of this.dutyNames) {
            let cd: ClassDuty = new ClassDuty();
            cd.customInitializer();
            cd.type = dutyname;
            this.rows.push(cd);
            this.classDuties.push(cd);
        }

        for (let entry of this.classRegister.duties) {
            this.rows[this.rows.findIndex(e => e.type == entry.type)] = entry;
        }

        for (let entry of this.rows) {
            this.isPupil[entry.type] = entry.main == 0 && entry.mainText == "" || entry.main != 0;
        }

        this.table = new TableData([
            new TableColumn("type", "Klassenamt", TableCellType.string, 30),
            new TableColumn("isPupil", "Ist Schüler", TableCellType.bool, 1, {
                editable: true
            }),
            new TableColumn("main", "Vorsitz", TableCellType.string, 30, {
                editable: true,
                editor: new FrontendField("", "", FrontendFieldType.dropdown, {
                    listItems: this.pupilsGui
                }),
                valueFormatter: params => params.value == 0 ? "" : this.getPupil(params.value as number)
            }),
            new TableColumn("substitutes", "Stellvertreter", TableCellType.string, 30, {
                editable: true,
                editor: new FrontendField("", "", FrontendFieldType.dropdownMulti, {
                    listItems: this.pupilsGui
                }),
                valueFormatter: params => Utils.isNoe(params.value) ? "" : Arrays.itemsToString(this.getPupilList(params.value as number[]))
            })           
        ], this.rows.map(row => new TableRow(Objects.toString(row.id), row, {
            type: row.type,
            isPupil: this.isPupil[row.type],
            main: row.main,
            substitutes: row.substitutes 
        })));

        this.orgRows = Objects.cloneDeep(this.rows);

        this.app.updateNavigation(null, [
            { label: "Digitales Klassenbuch", routerLink: ["/enaio-certificates", "class-registers", "home"] },
            { label: "Klasse " + this.classRegister.enaioSchoolClass.getDisplayName(), routerLink: ["/enaio-certificates", "class-registers", "class-register", this.id] },
            { label: "Klassenämter", routerLink: ["/enaio-certificates", "class-registers", "class-duties", this.id] }]);

        return true;
    }

    override getCanDeactivateMessage(): string {
        return this.isChanged() ? "" : null;
    }

    isChanged(): boolean {
        return !Objects.equalsDeep(this.rows, this.orgRows);
    }

    async save(): Promise<void> {
        await this.app.saveDataHandler(async () => RestEndpoint.main().query({ id: this.id }).body(this.rows).post().run("api/cer/classregister/classDuties").getText(), this, true, true);
    }

    addRow(): void {
        this.rows.push(new ClassDuty());
    }

    changeClassDuty(event: any, row: ClassDuty): void {
        row.type = event;
    }

    changeMain(event: any, row: ClassDuty): void {
        if (typeof event == "string") {
            row.main = 0;
            row.mainText = event;
        }
        else {
            row.mainText = "";
            row.main = event;
        }
    }

    changeSubstitutes(event: any, row: ClassDuty): void {
        row.substitutes = event;
    }

    getPupil(pupilId: number): string {
        let pupil: Pupil = this.classPupils.find(p => p.id == pupilId);
        return pupil.getFullName();
    }

    getPupilList(pupilIds: number[]): string[] {
        let list: string[] = [];

        for (let pId of pupilIds) {
            let pupil: Pupil = this.classPupils.find(p => p.id == pId);
            list.push(pupil.getFullName());
        }
        return list;
    }

    changeIsPupil(event: any, rowType: string): void {
        let row: ClassDuty = this.rows.find(r => r.type == rowType);
        if (event.checked) {
            row.mainText = "";
            row.substitutesText = "";
        }
        else {
            row.main = 0;
            row.substitutes.splice(0);
        }
    }
}
